import { useState, useEffect, createContext, useContext, useMemo } from 'react'

type UserEnvironmentContextValue = {
  prefersReducedMotion: boolean
}
const UserEnvironmentContext =
  createContext<UserEnvironmentContextValue | null>(null)
UserEnvironmentContext.displayName = 'UserEnvironmentContext'

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(
    () => window.matchMedia(REDUCED_MOTION_QUERY).matches
  )

  useEffect(() => {
    const query = window.matchMedia(REDUCED_MOTION_QUERY)
    const listener = (event: MediaQueryListEvent) =>
      setPrefersReducedMotion(event.matches)
    query.addEventListener('change', listener)
    return () => query.removeEventListener('change', listener)
  }, [])

  return prefersReducedMotion
}

export const UserEnvironmentProvider = ({
  children,
}: {
  children?: React.ReactNode
}) => {
  const prefersReducedMotion = usePrefersReducedMotion()
  const value = useMemo(
    () => ({ prefersReducedMotion }),
    [prefersReducedMotion]
  )

  return (
    <UserEnvironmentContext.Provider value={value}>
      {children}
    </UserEnvironmentContext.Provider>
  )
}
UserEnvironmentProvider.displayName = 'UserEnvironmentProvider'

export function useUserEnvironment(): UserEnvironmentContextValue {
  const context = useContext(UserEnvironmentContext)
  if (!context) {
    throw new Error(
      'useUserEnvironment must be used within UserEnvironmentProvider'
    )
  }
  return context
}
